import { FocusEvent, Reducer, Ref, useCallback } from 'react';

import { CheckboxState } from '@adsk/alloy-react-checkbox';
import { useForwardedRef } from '@adsk/alloy-react-helpers';

import { getLastVisibleNode } from './helpers';
import useTreeReducer from './useTreeReducer';
import { TREE_ACTIONS } from '../consts';
import {
  IdType,
  DefaultInputTreeNode,
  InternalDataNode,
  TreeSelectedIds,
  TreeState,
  DefaultAction,
  BaseAction,
} from '../types';

type ExpandableProps =
  | { initialExpandAll?: boolean; initialExpandedIds?: never }
  | { initialExpandAll?: never; initialExpandedIds?: Record<IdType, boolean> };
export type UseTreeProps<
  InputTreeNode extends DefaultInputTreeNode = DefaultInputTreeNode,
  CustomAction extends DefaultAction = BaseAction<InputTreeNode>,
  TreeElement extends HTMLElement = HTMLUListElement,
> = ExpandableProps & {
  ref?: Ref<TreeElement | null>;
  denormalizedTree: InputTreeNode[];
  getIdFromNode?: (node: InputTreeNode) => IdType;
  getLabelFromNode?: (node: InputTreeNode) => string;
  getChildrenFromNode?: (node: InputTreeNode) => InputTreeNode[] | undefined;
  initialHighlightedId?: IdType;
  selectedIds?: TreeSelectedIds;
  onSelect?: (p: { isSelected: CheckboxState; id: IdType }) => void;
  reducer?: Reducer<
    TreeState<InputTreeNode>,
    BaseAction<InputTreeNode> | CustomAction
  >;
};

/** Use Tree DESCRIPTION HERE */
const useTree = <
  InputTreeNode extends DefaultInputTreeNode,
  CustomAction extends DefaultAction = BaseAction<InputTreeNode>,
  TreeElement extends HTMLElement = HTMLUListElement,
>({
  ref,
  denormalizedTree,
  getIdFromNode,
  getLabelFromNode,
  getChildrenFromNode,
  initialExpandAll,
  initialExpandedIds,
  initialHighlightedId,
  selectedIds,
  onSelect,
  reducer: customReducer,
}: UseTreeProps<InputTreeNode, CustomAction, TreeElement>) => {
  const {
    state: { normalizedTree, highlightedId, orderedIds, expandedIds },
    dispatch,
  } = useTreeReducer<InputTreeNode, CustomAction>({
    denormalizedTree,
    initialExpandAll,
    initialExpandedIds,
    getIdFromNode,
    getLabelFromNode,
    getChildrenFromNode,
    initialHighlightedId,
    customReducer,
  });

  const defaultOnExpand = useCallback(
    ({
      isExpanded,
      id,
      recurse,
    }: {
      isExpanded: boolean;
      id: IdType;
      recurse?: boolean;
    }) => {
      const payload = { id };
      if (recurse) {
        return isExpanded
          ? dispatch({ type: TREE_ACTIONS.expandNodeRecursive, payload })
          : dispatch({ type: TREE_ACTIONS.collapseNodeRecursive, payload });
      } else {
        return isExpanded
          ? dispatch({ type: TREE_ACTIONS.expandNode, payload })
          : dispatch({ type: TREE_ACTIONS.collapseNode, payload });
      }
    },
    [dispatch],
  );

  const highlightNode = useCallback(
    ({ id, isHoverHighlight }: { id?: IdType; isHoverHighlight?: boolean }) => {
      dispatch({
        type: TREE_ACTIONS.highlightNode,
        payload: { id, isHoverHighlight },
      });
    },
    [dispatch],
  );

  const getisLastAtDepth = useCallback(
    ({ id, path }: InternalDataNode) => {
      const parentId = path[path.length - 1];

      if (!parentId) {
        return false;
      }

      const parent = normalizedTree[parentId];

      return parent?.childIds.indexOf(id) === parent.childIds.length - 1;
    },
    [normalizedTree],
  );

  const getTreeNodeProps = useCallback(
    ({ id, show, label, childIds, path, original }: InternalDataNode) => ({
      id,
      role: 'treeitem',
      'data-testid': `TreeNode_${id}`,
      'aria-label': label,
      'aria-expanded': expandedIds[id],
      'aria-selected': !!selectedIds?.[id],
      'aria-hidden': !show,
      show,
      label,
      depth: path.length,
      isLastAtDepth: getisLastAtDepth({
        id,
        show,
        label,
        childIds,
        path,
        original,
      }),
      isExpanded: expandedIds[id],
      isSelected: selectedIds?.[id],
      isExpandable: childIds.length > 0,
      isHighlighted: highlightedId === id,
      onExpand: defaultOnExpand,
      onSelect,
      onMouseEnter: () => highlightNode({ id, isHoverHighlight: true }),
      onBlur: (e: FocusEvent) => {
        // Don't trigger parent's onBlur or we'll unhighlight the node
        e.stopPropagation();
      },
    }),
    [
      expandedIds,
      selectedIds,
      getisLastAtDepth,
      highlightedId,
      defaultOnExpand,
      onSelect,
      highlightNode,
    ],
  );

  const treeRef = useForwardedRef(ref || null);
  const getTreeProps = useCallback(
    () => ({
      role: 'tree',
      ref: treeRef,
      tabIndex: 0,
      style: { minWidth: 200, outline: 'none' },
      orderedIds,
      normalizedTree,
      getTreeNodeProps,
      onFocus: () => {
        if (highlightedId) {
          return;
        }

        /* ARIA focus logic:
         * https://www.w3.org/TR/wai-aria-practices-1.1/#keyboard-interaction-22
         *
         * When a single-select tree receives focus:
         *  - If none of the nodes are selected before the tree receives focus,
         *  focus is set on the first node.
         *  - If a node is selected before the tree receives focus, focus is set
         *  on the selected node.
         *
         * When a multi-select tree receives focus:
         *  - If none of the nodes are selected before the tree receives focus,
         *  focus is set on the first node.
         *  - If one or more nodes are selected before the tree receives focus,
         *  focus is set on the first selected node.
         */
        const selectedNodeId = selectedIds
          ? orderedIds.find((id) => {
              return selectedIds[id] === true;
            })
          : undefined;
        highlightNode({ id: selectedNodeId || orderedIds[0] });
      },
      onBlur: (e: FocusEvent) => {
        if (e.target.contains(e.relatedTarget)) {
          return;
        }

        highlightNode({});
      },
      onMouseLeave: () => highlightNode({ isHoverHighlight: true }),
      onKeyDown: (event: React.KeyboardEvent<HTMLElement>) => {
        const key = event.key;
        const currentNode = highlightedId
          ? normalizedTree[highlightedId]
          : undefined;

        const { childIds, id, path } = currentNode || {};
        const isExpanded = id && expandedIds[id];
        const isExpandable = childIds ? childIds.length > 0 : false;
        if (treeRef.current && document.activeElement !== treeRef.current) {
          treeRef.current.focus();
        }

        switch (key) {
          case 'ArrowLeft':
            if (!id) {
              break;
            }

            if (isExpanded) {
              const payload = { id };

              const dispatchValue = event.shiftKey
                ? { type: TREE_ACTIONS.collapseNodeRecursive, payload }
                : { type: TREE_ACTIONS.collapseNode, payload };

              dispatch(dispatchValue);
              break;
            }

            const parentNodeId = path ? path[path.length - 1] : undefined;
            highlightNode({ id: parentNodeId });

            break;
          case 'ArrowRight':
            if (!id) {
              break;
            }

            if (isExpanded) {
              dispatch({
                type: TREE_ACTIONS.moveDown,
                payload: { id },
              });
              break;
            }
            if (event.shiftKey) {
              dispatch({
                type: TREE_ACTIONS.expandNodeRecursive,
                payload: { id },
              });
              break;
            }

            dispatch({
              type: TREE_ACTIONS.expandNode,
              payload: { id },
            });
            break;
          case 'ArrowDown':
            dispatch({
              type: TREE_ACTIONS.moveDown,
              payload: { id },
            });
            break;
          case 'ArrowUp':
            dispatch({
              type: TREE_ACTIONS.moveUp,
              payload: { id },
            });
            break;
          case 'Home':
            highlightNode({ id: orderedIds[0] });
            break;
          case 'End':
            const lastVisibleNode = getLastVisibleNode({
              orderedIds,
              normalizedTree,
            });
            highlightNode({ id: lastVisibleNode?.id });
            break;
          case ' ': // Space
            if (id && onSelect && selectedIds) {
              onSelect({ id, isSelected: !selectedIds[id] });
            }
            break;
          case 'Enter':
            if (!id) {
              break;
            }

            if (isExpandable) {
              const payload = { id };

              if (event.shiftKey) {
                const dispatchValue = isExpanded
                  ? { type: TREE_ACTIONS.collapseNodeRecursive, payload }
                  : { type: TREE_ACTIONS.expandNodeRecursive, payload };

                dispatch(dispatchValue);
                break;
              }

              const dispatchValue = isExpanded
                ? { type: TREE_ACTIONS.collapseNode, payload }
                : { type: TREE_ACTIONS.expandNode, payload };

              dispatch(dispatchValue);
              break;
            }

            if (onSelect && selectedIds) {
              onSelect({ id, isSelected: !selectedIds[id] });
            }
            break;
          default:
            break;
        }
      },
    }),
    [
      treeRef,
      orderedIds,
      expandedIds,
      getTreeNodeProps,
      highlightedId,
      highlightNode,
      normalizedTree,
      onSelect,
      selectedIds,
      dispatch,
    ],
  );
  return {
    dispatch,
    orderedIds,
    expandedIds,
    normalizedTree,
    highlightedId,
    getTreeProps,
    getTreeNodeProps,
  };
};

export default useTree;
